<template>
  <span
    v-if="hasComments"
    ref="comment"
    v-clickOutside="close"
    :content="node.attrs.content"
    :contentid="node.attrs.contentid"
    class="absolute comment-content left-full font-Inter"
    @click.prevent="toggleComment"
  >
    <!-- ICON -->
    <span
      ref="icon"
      :style="iconPosition"
      class="icon-chat comment-icon flex text-app-color text-14 opacity-40 anim-all hover:opacity-100 cursor-pointer px-10 py-6 rounded-6"
      :class="iconClasses"
      @mouseover.stop.prevent="highlightComment(true)"
      @mouseleave.stop.prevent="highlightComment(false)"
    >
      <span class="font-Inter ml-3 comment-icon-counter">{{ comments.length }}</span>
    </span>

    <!-- Backdrop -->
    <transition name="fade">
      <div
        v-if="showComment && isMobile"
        class="fixed z-50 bg-backdrop w-full h-full flex top-0 left-0"
        @click="closeMobileModal"
      />
    </transition>

    <!-- MODAL -->
    <portal to="editor">
      <div
        v-show="showComment"
        ref="iconModal"
        :style="modalPosition"
        class="m-auto pt-15 w-full max-w-320 rounded-4 fixed md:absolute md:w-270 md:shadow z-9999"
        :class="isDarkMode ? 'bg-sidebar' : 'bg-white'"
        @click.stop
      >
        <div class="flex items-center justify-between mb-10 md:pl-20 px-15">
          <p class="font-bold text-17 text-app-color">Comments</p>
          <ButtonModule
            text="Resolved"
            class="bg-black-main text-white text-12 rounded-4 py-7 px-10 opacity-25 hover:opacity-100"
            @click="remove"
          />
        </div>

        <div ref="scrollToDownRef" class="max-h-300 overflow-auto">
          <commentItem
            v-for="item in comments"
            :key="item.id"
            class="md:pl-20 px-15"
            :comment="item"
            :comments="comments"
            :project="project"
            :user="user"
            @remove="remove"
          />

        </div>
        <div class="border-t border-hover flex items-start justify-between py-15 md:pl-20 px-15">
          <div class="my-auto flex mr-10">
            <ExpandedTextarea>
              <textarea
                ref="textareaRef"
                v-model="comment"
                maxlength="255"
                style="min-height: 28px"
                rows="1"
                placeholder="Type here..."
                class="w-full outline-none bg-transparent text-app-color font-normal-important no-resize text-15"
                @keydown.enter.exact.prevent.stop="add"
              />
            </ExpandedTextarea>
          </div>

          <ButtonModule
            text="Comment"
            class="bg-link-color rounded-4 px-16 py-12 text-white text-14 font-semibold hover:opacity-90 ease-in-out"
            @click="add"
          />
        </div>
      </div>
    </portal>
  </span>
</template>

<script>
import ButtonModule from '@/components/shared/form-controls/ButtonModule'

import { commentService } from '@/services'
import { mapState, mapMutations, mapGetters } from 'vuex'
import { selectText, unselectText } from '@/core/helper-functions'
import commentItem from '@/tiptap/extensions/commentItem'
import ExpandedTextarea from '@/components/shared/form-controls/ExpandedTextarea'
import { defineComponent } from 'vue'

export default defineComponent({
  components: { ButtonModule, commentItem, ExpandedTextarea },

  props: {
    node: { type: Object },
    editor: { type: Object }
  },

  data () {
    return {
      newComment: '',
      comment: '',
      showComment: false,
      isEdit: false,
      iconPosition: 'right: -10px',
      modalPosition: 'top: -100%; left: -100%'
    }
  },

  computed: {
    ...mapState({
      project: state => state.project.currentProject,
      share: state => state.project.share,
      user: state => state.profile.user
    }),

    ...mapGetters(['isMobile', 'isDarkMode']),

    notAllowDelete () {
      return this.$route.name === 'Share' && this.share.mode === 'read'
    },

    hasComments () {
      return this.project?.comments.length > 0
    },

    iconClasses () {
      const prefix = this.showComment ? 'opacity-100 ' : 'hover:'
      return this.isDarkMode ? `${prefix}bg-dark-500` : `${prefix}bg-menu-hover`
    },

    comments () {
      if (!this.project) return []

      return this.project.comments.filter(
        item => item.id === this.node.attrs.contentid ||
        item.parentId === this.node.attrs.contentid
      ).sort((a, b) => {
        if (a.id > b.id) return 1
        if (a.id < b.id) return -1
        return 0
      })
    },

    allowResolve () {
      return this.user &&
        this.currentComment?.user &&
        this.currentComment.user.profile.userName === this.user.profile.userName
    },

    currentComment () {
      return this.project?.comments.find(item => item.id === this.node.attrs.contentid)
    }
  },

  created () {
    this.$nextTick(() => {
      const coords = this.$refs.icon.getBoundingClientRect()
      if (window.innerWidth - coords.right > 40) this.iconPosition = this.isMobile ? 'right: -30px' : 'right: -40px'
    })
  },

  methods: {
    ...mapMutations(['loading', 'updateComments']),

    highlightComment (value) {
      const comment = document.querySelector(`span[commentid="${this.node.attrs.contentid}"]`)

      if (value) {
        comment.classList.add('highlight')
      } else {
        comment.classList.remove('highlight')
      }
    },

    scrollToElement () {
      this.$nextTick(() => {
        if (this.$refs.scrollToDownRef) {
          this.$refs.scrollToDownRef.scrollTop = this.$refs.scrollToDownRef.scrollHeight
        }
      })
    },

    add () {
      if (this.comment.trim() === '') return

      const data = {
        body: this.comment,
        parentId: this.node.attrs.contentid
      }

      if (this.$route.name === 'Share') {
        if (this.share.mode === 'read') return

        commentService.createWithShare(data, this.project.id, this.share.id)
          .then(({ data }) => {
          // save comment to store
            const comments = this.project.comments
            comments.push(data.dataValues)
            this.updateComments(comments)

            this.comment = ''
            this.scrollToElement()
          })
      } else {
        commentService.create(data, this.project.id)
          .then(({ data }) => {
          // save comment to store
            const comments = this.project.comments
            const comment = {
              ...data.dataValues,
              user: this.user
            }

            comments.push(comment)
            this.updateComments(comments)

            this.comment = ''
            this.scrollToElement()
          })
      }
    },

    setModalPosition () {
      const editor = document.getElementById('editor-wrapper')
      const editorRect = editor?.getBoundingClientRect()
      const iconRect = this.$refs.icon.getBoundingClientRect()
      const iconModal = this.$refs.iconModal.getBoundingClientRect()

      let top = iconRect.top - editorRect.y - iconModal.height - 10

      if (top < 0) {
        top = iconRect.top - editorRect.y + 20
      }

      this.modalPosition = `left: ${iconRect.right - iconModal.width - editorRect.x}px; top: ${top}px`
    },

    toggleComment () {
      this.showComment = !this.showComment

      this.editor.chain().blur()
      this.scrollToElement()
      setTimeout(() => {
        if (this.showComment) {
          if (this.isMobile) {
            this.modalPosition = 'left: 50%; top: 50%; transform: translate(-50%, -50%);'
          } else {
            this.setModalPosition()
          }

          setTimeout(() => {
            this.$refs.textareaRef.focus()
          }, 100)
        }
      }, 200)
    },

    close () {
      this.modalPosition = 'top: -100%; left: -100%'
      this.showComment = false
    },

    closeMobileModal () {
      this.showComment = false

      this.editor.chain().blur()
    },

    remove () {
      if (this.$route.name === 'Share' && this.share.mode === 'read') return

      const mark = document.querySelector(`span[commentid="${this.node.attrs.contentid}"]`)
      const node = document.querySelector(`span[contentid="${this.node.attrs.contentid}"]`)
      selectText(mark)

      setTimeout(() => {
        node.parentNode.remove()
        const id = this.node.attrs.contentid

        if (this.currentComment) {
          commentService.delete(id).then(() => {
            this.close()
            this.updateComments(this.project.comments.filter(item => item.id !== id))
            this.editor.commands.unsetMark('commentMark')
            unselectText()
          })
        } else {
          this.editor.commands.unsetMark('commentMark')
          unselectText()
        }
      }, 100)
    }
  }
})
</script>
